import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadBytesResumable,
} from 'firebase/storage';
import { ModalService } from 'npx-family-happy-common';
import { catchError, debounceTime, map, of, Subject, takeUntil } from 'rxjs';
import { UserStorageService } from 'src/app/authentication/user-storage/user-storage.service';
import { CaregiverEvaluationRecord } from 'src/app/shared/models/caregiver-evaluation-record.model';
import {
  CaregiverEvaluation,
  DEFAULT_CAREGIVER_EVALUATION,
} from 'src/app/shared/models/caregiver-evaluation.model';
import { EvaluationService } from 'src/app/shared/services/evaluation-service/evaluation.service';
import {
  PsychologistState,
  RouterStateManagerService,
} from 'src/app/shared/services/router-state-manager/router-state-manager.service';

import { ImageCropperComponent } from 'src/app/shared/components/image-cropper/image-cropper.component';
import { AlertComponent } from 'src/app/shared/components/modals/alert/alert.component';
import { DEFAULT_CAREGIVER_ENGAGEMENT } from 'src/app/shared/models/caregiver-engagement.model';
import { cellFormatter, emailFormatter } from 'src/app/shared/utils.functions';
import { Psychologist } from '../../../shared/models/psychologist.model';
import { PsychologistService } from '../psychologist-service/psychologist-service.service';

// * PSYCHOLOGIST SPECIALTIES
// UMORE
// ANSIA
// STRESS
// AUTOSTIMA
// AFFETTIVITA_E_RELAZIONI
// DIPENDENZE
// LAVORO
// CRESCITA PERSONALE
// RAGGIUNGIMENTO OBBIETTIVI
// FOBIE,
// ALIMENTAZIONE,
// SESSUALITÀ,
// GENITORIALITÀ,
// LUTTI,
// TRAUMI,

@Component({
  selector: 'app-psychologist-detail',
  templateUrl: './psychologist-detail.component.html',
  styleUrls: ['./psychologist-detail.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AlertComponent,
    ImageCropperComponent,
  ],
})
export class PsychologistDetailComponent implements OnInit {
  isNew = true;
  hasPayed!: boolean;
  addressSelected: boolean = false;
  showAlert = false;
  showModal = false;
  chosenPhoto!: string;
  finalPhoto!: string;
  step = 0;
  form!: UntypedFormGroup;
  psychologist!: Psychologist;
  psychologistID!: string;
  generatePages: boolean = false;
  cvPages: number = 1;
  currentPage: number = 0;
  canvases: HTMLCanvasElement[] = [];
  checkNotRated = true;
  private nextTab = -1;
  private psychologistState!: PsychologistState;
  private unsubscribe = new Subject<void>();
  private caregiverRating: CaregiverEvaluation | undefined;
  private currentUser: string = '';
  private goBackRequested = false;

  //default option for NEW PSYCHOLOGIST
  defaultRating = DEFAULT_CAREGIVER_EVALUATION;
  defaultEngagement = DEFAULT_CAREGIVER_ENGAGEMENT;

  constructor(
    private caregiverEvaluationService: EvaluationService,
    private psychologistService: PsychologistService,
    private stateManager: RouterStateManagerService,
    private sanitizer: DomSanitizer,
    private modalService: ModalService,
    private activeRoute: ActivatedRoute,
    private userStorage: UserStorageService
  ) {
    this.finalPhoto = '';
  }

  ngOnInit(): void {
    this.userStorage
      .getUser()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((user: any) => {
        this.currentUser = user?.name ?? '';
      });
    this.form = new UntypedFormGroup({
      doc_ref: new UntypedFormControl(null),
      certificate: new UntypedFormControl(null),
      info: new UntypedFormGroup({
        name: new UntypedFormControl(null, Validators.required),
        surname: new UntypedFormControl(null, Validators.required),
        email: new UntypedFormControl(null, Validators.required),
        phone: new UntypedFormControl(null, Validators.required),
        fiscalcode: new UntypedFormControl(null),
        iban: new UntypedFormControl(null),
        resume: new UntypedFormControl(null),
        video: new UntypedFormControl(null),
        notes: new UntypedFormControl(null),
        resumeHidden: new UntypedFormControl(null),
        description: new UntypedFormControl(null),
        calendarLink: new UntypedFormControl(null),
        photo: new UntypedFormControl(null),
        stopWhatsapp: new UntypedFormControl(false),
        subscription: new UntypedFormControl(null),
        expDate: new UntypedFormControl(null),
        manualSubscription: new UntypedFormControl(null),
        expDateManual: new UntypedFormControl(null),
      }),

      lang: new UntypedFormControl(null),
      updateTimestamp: new UntypedFormControl(null),
      umore: new UntypedFormControl(false),
      ansia: new UntypedFormControl(false),
      stress: new UntypedFormControl(false),
      autostima: new UntypedFormControl(false),
      affettivita_e_relazioni: new UntypedFormControl(false),
      dipendenze: new UntypedFormControl(false),
      lavoro: new UntypedFormControl(false),
      crescita_personale: new UntypedFormControl(false),
      raggiungimento_obbiettivi: new UntypedFormControl(false),
      fobie: new UntypedFormControl(false),
      alimentazione: new UntypedFormControl(false),
      sessualità: new UntypedFormControl(false),
      genitorialità: new UntypedFormControl(false),
      lutti: new UntypedFormControl(false),
      traumi: new UntypedFormControl(false),

      // active: new UntypedFormControl(true),
    });
    this.activeRoute.params.subscribe((param) => {
      this.psychologistID = param.id;
    });
    try {
      this.stateManager
        .getPsychologistState()
        .pipe(takeUntil(this.unsubscribe))
        .subscribe((state: any) => {
          this.psychologistState = state;
          this.psychologistState.id = this.psychologistID;
          this.psychologistState.route =
            'psychologist-detail/' + this.psychologistID;
          if (this.psychologistState.step >= 0) {
            this.step = this.psychologistState.step;
          }
          if (this.psychologistState.psychologist) {
            this.isNew = false;
            this.setPsychologist(this.psychologistState.psychologist);
            this.formInitialize(this.psychologistState.psychologist);
          } else if (!this.psychologistID.match('null')) {
            const id = this.psychologistID;
            if (id && id !== 'new') {
              this.isNew = false;
              this.psychologistService
                .getSinglePsychologist(id)
                .pipe(takeUntil(this.unsubscribe))
                .subscribe((psychologists) => {
                  let psychologist = psychologists[0];
                  if (psychologist) {
                    this.setPsychologist(psychologist);
                    this.formInitialize(this.psychologist);
                  }
                });
            }
          }
        });
    } catch (err) {
      this.modalService.showErrorMessage(
        'Errore nel recupero delle informazioni legate al nutrizionista.'
      );
    }

    this.form.valueChanges
      .pipe(debounceTime(300), takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this.psychologist = {
          docRef: value.doc_ref?.toString().trim() || null,
          certificate: value.certificate?.toString().trim() || null,
          info: {
            name: value.info.name?.toString().trim() || null,
            surname: value.info.surname?.toString().trim() || null,
            email: value.info.email?.toString().trim() || null,
            phone: value.info.phone?.toString().trim() || null,
            fiscalcode: value.info.fiscalcode?.toString().trim() || null,
            iban: value.info.iban?.toString().trim() || null,
            resume: value.info.resume?.toString().trim() || null,
            video: value.info.video?.toString().trim() || null,
            notes: value.info.notes?.toString().trim() || null,
            resumeHidden: value.info.resumeHidden?.toString().trim() || null,
            description: value.info.description?.toString().trim() || null,
            calendarLink: value.info.calendarLink?.toString().trim() || null,
            photo: value.info.photo?.toString().trim() || null,
            stopWhatsapp: Boolean(value.info.stopWhatsapp),
            subscription: value.info.subscription?.toString().trim() || null,
            expDate: value.info.expDate?.toString().trim() || null,
            manualSubscription:
              value.info.manualSubscription?.toString().trim() || null,
            expDateManual: value.info.expDateManual?.toString().trim() || null,
          },
          lang: value.lang?.toString().trim() || null,
          updateTimestamp: value.updateTimestamp?.toString().trim() || null,
          specialties: [],
          // active: value.active,
        };
      });
  }

  uploadFile(_event: any, field: string) {
    try {
      this.modalService.showLoadingWithMessage('Caricamento CV in corso...');
      const file = <File>_event.target.files[0];
      const path =
        field + '/' + field + this.form.get('info.email')?.value + '.pdf';
      let storageRef = ref(getStorage(), path);
      const uploadTask = uploadBytesResumable(storageRef, file);
      uploadTask.on(
        'state_changed',
        () => {},
        () => {},
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
            if (field === 'info.resume') {
              this.psychologist.info.resume = downloadURL;
            } else {
              this.psychologist.info.resumeHidden = downloadURL;
            }
            this.form.get(field)?.setValue(downloadURL);
            if (this.isNew) {
              this.createPsychologist();
              this.modalService.hideLoading();
              this.modalService.showSuccessMessage('Caricamento riuscito.');
            } else {
              this.updatePsychologist();
              this.modalService.hideLoading();
              this.modalService.showSuccessMessage('Caricamento riuscito.');
            }
          });
        }
      );
    } catch (err) {
      this.modalService.hideLoading();
      this.modalService.showErrorMessage(
        'Errore nel caricamento del CV. Si prega di riprovare'
      );
    }
  }

  uploadImage(image: string, id: string) {
    try {
      this.modalService.showLoadingWithMessage('Caricamento foto in corso...');
      const file = this.base64ToBlob(
        image.replace('data:image/png;base64,', ''),
        'image/png'
      );
      const path = 'pictures/' + id + '.png';
      let storageRef = ref(getStorage(), path);
      const uploadTask = uploadBytesResumable(storageRef, file);
      uploadTask.on(
        'state_changed',
        () => {},
        () => {},
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
            this.form.get('info.photo')?.setValue(downloadURL);
            let body = this.psychologist.info;
            body.photo = downloadURL;
            this.psychologist.info = body;
            if (this.isNew) {
              this.createPsychologist();
              this.modalService.hideLoading();
              this.modalService.showSuccessMessage('Caricamento riuscito.');
            } else {
              this.updatePsychologist();
              this.modalService.hideLoading();
              this.modalService.showSuccessMessage('Caricamento riuscito.');
            }
          });
        }
      );
    } catch (err) {
      this.modalService.hideLoading();
      this.modalService.showErrorMessage(
        'Errore nel corso del caricamento della foto. Si prega di riprovare.'
      );
    }
    this.showModal = false;
  }

  base64ToBlob(base64Data: string, contentType: string) {
    contentType = contentType || '';
    let sliceSize = 1024;
    let byteCharacters = atob(base64Data);
    let bytesLength = byteCharacters.length;
    let slicesCount = Math.ceil(bytesLength / sliceSize);
    let byteArrays = new Array(slicesCount);

    for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
      let begin = sliceIndex * sliceSize;
      let end = Math.min(begin + sliceSize, bytesLength);

      let bytes = new Array(end - begin);
      for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
        bytes[i] = byteCharacters[offset].charCodeAt(0);
      }
      byteArrays[sliceIndex] = new Uint8Array(bytes);
    }
    return new Blob(byteArrays, { type: contentType });
  }

  goBack() {
    if (this.form.dirty) {
      this.showAlert = true;
      this.goBackRequested = true;
    } else {
      this.psychologistState.route = 'psychologist-list';
      this.psychologistState.psychologist = undefined;
      this.psychologistState.step = 0;
      this.psychologistState.id = 'null';
      this.ngOnDestroy();
      this.stateManager.setPsychologistState(this.psychologistState);
    }
  }

  formInitialize(psychologist: Psychologist) {
    if (psychologist) {
      this.form.setValue({
        doc_ref: psychologist.docRef,
        certificate: psychologist.certificate,
        info: {
          name: psychologist.info.name,
          surname: psychologist.info.surname,
          email: psychologist.info.email
            ? emailFormatter(psychologist.info.email as string)
            : null,
          phone: psychologist.info.phone
            ? cellFormatter(psychologist.info.phone as string)
            : null,
          fiscalcode: psychologist.info.fiscalcode,
          iban: psychologist.info.iban,
          resume: psychologist.info.resume,
          video: psychologist.info.video,
          notes: psychologist.info.notes,
          resumeHidden: psychologist.info.resumeHidden,
          description: psychologist.info.description,
          calendarLink: psychologist.info.calendarLink,
          photo: psychologist.info.photo,
          stopWhatsapp: psychologist.info.stopWhatsapp,
          subscription: psychologist.info.subscription,
          expDate: psychologist.info.expDate,
          manualSubscription: psychologist.info.manualSubscription,
          expDateManual: psychologist.info.expDateManual,
        },
        lang: psychologist.lang,
        updateTimestamp: psychologist.updateTimestamp,
        umore: psychologist.specialties.includes('UMORE'),
        ansia: psychologist.specialties.includes('ANSIA'),
        stress: psychologist.specialties.includes('STRESS'),
        autostima: psychologist.specialties.includes('AUTOSTIMA'),
        affettivita_e_relazioni: psychologist.specialties.includes(
          'AFFETTIVITA_E_RELAZIONI'
        ),
        dipendenze: psychologist.specialties.includes('DIPENDENZE'),
        lavoro: psychologist.specialties.includes('LAVORO'),
        crescita_personale:
          psychologist.specialties.includes('CRESCITA_PERSONALE'),
        raggiungimento_obbiettivi: psychologist.specialties.includes(
          'RAGGIUNGIMENTO_OBBIETTIVI'
        ),
        fobie: psychologist.specialties.includes('FOBIE'),
        alimentazione: psychologist.specialties.includes('ALIMENTAZIONE'),
        sessualità: psychologist.specialties.includes('SESSUALITÀ'),
        genitorialità: psychologist.specialties.includes('GENITORIALITÀ'),
        lutti: psychologist.specialties.includes('LUTTI'),
        traumi: psychologist.specialties.includes('TRAUMI'),
      });
    }
  }

  save(showMessage?: boolean) {
    if (this.form.valid) {
      if (this.isNew) {
        this.createPsychologist();
      } else {
        this.updatePsychologist(showMessage);
      }
    }
  }

  changeTab(tab: number) {
    if (this.form.dirty) {
      this.showAlert = true;
      this.nextTab = tab;
    } else {
      this.step = tab;
      this.psychologistState.step = this.step;
    }
  }

  onAlertAction(save: boolean) {
    if (save && !this.isNew) {
      this.updatePsychologist();
    } else if (save && this.isNew) {
      this.createPsychologist();
    } else if (!save && this.goBackRequested) {
      if (this.psychologist === undefined) {
        this.form.markAsPristine();
        this.form.reset();
        this.goBack();
      } else {
        this.form.markAsPristine();
        this.form.reset();
        this.formInitialize(this.psychologist);
        this.goBack();
      }
    } else {
      this.form.markAsPristine();
      this.form.reset();
      this.formInitialize(this.psychologist);
      this.goBack();
    }
    this.showAlert = false;
    if (this.nextTab !== -1) {
      this.step = this.nextTab;
      this.psychologistState.step = this.step;
      this.nextTab = -1;
    }
  }

  private async createPsychologist() {
    this.psychologist = await this.processFormData(this.form.value);
    this.psychologistService
      .createPsychologist(this.psychologist)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (res: any) => {
          this.modalService.showSuccessMessage('Nuovo nutrizionista creato.');
          const regex = /#(\w{2}-\d{5})/gm;
          const result = regex.exec(res.message);
          if (result) {
            this.psychologistService
              .getSinglePsychologist(result[1])
              .pipe(takeUntil(this.unsubscribe))
              .subscribe((psychologists) => {
                let psychologist = psychologists[0];
                if (psychologist) {
                  this.isNew = !this.isNew;
                  this.psychologist = psychologist;
                  this.form.markAsPristine();
                  this.formInitialize(this.psychologist);
                }
              });
          }
        },
        error: (error) => {
          if ((error.error as string).match('Key .* already exists.')) {
            this.modalService.showErrorMessage(
              "Esiste già un profilo associato all'indirizzo email fornito."
            );
          } else {
            this.modalService.showErrorMessage(
              'Si è verificato un errore nella creazione della psychologist, si prega di riprovare.'
            );
          }
        },
      });
  }

  private async updatePsychologist(showMessage?: boolean) {
    this.psychologist = await this.processFormData(this.form.value);
    this.psychologistService
      .updatePsychologist(this.psychologist)
      .pipe(
        takeUntil(this.unsubscribe),
        map(() => {
          this.publishNewCaregiverScoreUpdate();
          this.form.markAsPristine();
          this.formInitialize(this.psychologist);
          if (showMessage) {
            this.modalService.showSuccessMessage('Nutrizionista modificato.');
          }
        }),
        catchError((error) => {
          this.modalService.showErrorMessage(
            `Si è verificato un errore nell'aggiornamento del nutrizionista.`
          );
          return of();
        })
      )
      .subscribe();
  }

  private async processFormData(data: any) {
    const processedSpecialties: string[] = [];
    if (data.umore) {
      processedSpecialties.push('UMORE');
    }
    if (data.ansia) {
      processedSpecialties.push('ANSIA');
    }
    if (data.stress) {
      processedSpecialties.push('STRESS');
    }
    if (data.autostima) {
      processedSpecialties.push('AUTOSTIMA');
    }
    if (data.affettivita_e_relazioni) {
      processedSpecialties.push('AFFETTIVITA_E_RELAZIONI');
    }
    if (data.dipendenze) {
      processedSpecialties.push('DIPENDENZE');
    }
    if (data.lavoro) {
      processedSpecialties.push('LAVORO');
    }
    if (data.crescita_personale) {
      processedSpecialties.push('CRESCITA_PERSONALE');
    }
    if (data.raggiungimento_obbiettivi) {
      processedSpecialties.push('RAGGIUNGIMENTO_OBBIETTIVI');
    }
    if (data.fobie) {
      processedSpecialties.push('FOBIE');
    }
    if (data.alimentazione) {
      processedSpecialties.push('ALIMENTAZIONE');
    }
    if (data.sessualità) {
      processedSpecialties.push('SESSUALITÀ');
    }
    if (data.genitorialità) {
      processedSpecialties.push('GENITORIALITÀ');
    }
    if (data.lutti) {
      processedSpecialties.push('LUTTI');
    }
    if (data.traumi) {
      processedSpecialties.push('TRAUMI');
    }

    let psychologist: Psychologist = {
      docRef: data.doc_ref?.toString().trim() || null,
      certificate: data.certificate?.toString().trim() || null,
      info: {
        name: data.info.name?.toString().trim() || null,
        surname: data.info.surname?.toString().trim() || null,
        email: data.info.email?.toString().trim() || null,
        phone: data.info.phone?.toString().trim() || null,
        fiscalcode: data.info.fiscalcode?.toString().trim() || null,
        iban: data.info.iban?.toString().trim() || null,

        resume: data.info.resume?.toString().trim() || null,
        video: data.info.video?.toString().trim() || null,
        notes: data.info.notes?.toString().trim() || null,
        resumeHidden: data.info.resumeHidden?.toString().trim() || null,
        description: data.info.description?.toString().trim() || null,
        calendarLink: data.info.calendarLink?.toString().trim() || null,
        photo: data.info.photo?.toString().trim() || null,
        stopWhatsapp: Boolean(data.info.stopWhatsapp),
        subscription: data.info.subscription?.toString().trim() || null,
        expDate: data.info.expDate?.toString().trim() || null,
        manualSubscription:
          data.info.manualSubscription?.toString().trim() || null,
        expDateManual: data.info.expDateManual?.toString().trim() || null,
      },
      lang: data.lang?.toString().trim() || null,
      updateTimestamp: new Date().toISOString(),
      specialties: processedSpecialties,
      // active: data.active,
    };
    return psychologist;
  }

  private setPsychologist(bb: Psychologist) {
    this.psychologist = bb;
    this.psychologistState.psychologist = this.psychologist;
    this.psychologistState.step = this.step;
  }

  openModal(event: any) {
    let image = event.target.files[0];
    this.chosenPhoto = URL.createObjectURL(image);
    this.showModal = true;
  }

  sanitize(url: string) {
    return this.sanitizer.bypassSecurityTrustUrl(url);
  }

  dismiss(event: Event) {
    const elem = event.target as HTMLElement;
    if (elem === document.getElementById('modal-container')) {
      this.showModal = false;
    }
  }

  getCroppedPhoto(image: string) {
    this.finalPhoto = image;
  }

  cropperAbort() {
    let imageInput = <HTMLInputElement>(
      document.getElementById('image-selection')
    );
    imageInput.value = '';
    this.showModal = false;
  }

  htmlToCanvas(showAlert: boolean) {
    if (this.currentPage >= this.cvPages) {
      this.currentPage = 0;
      this.generatePages = false;
      return;
    }
    const element = document.getElementById('cv-page-container') as HTMLElement;
  }

  onCaregiverEvaluationChanged(newCaregiverEvaluation: CaregiverEvaluation) {
    this.caregiverRating = newCaregiverEvaluation;
    this.form.markAsTouched();
    this.form.markAsDirty();
  }

  private publishNewCaregiverScoreUpdate() {
    if (this.caregiverRating) {
      // Insert code to save new caregiver evaluation in the database
      const record: CaregiverEvaluationRecord = {
        ...this.caregiverRating,
        id: null,
        docRef: this.psychologist.docRef,
        operator: this.currentUser,
        timestamp: new Date(),
      };
      this.caregiverRating = undefined;
      this.caregiverEvaluationService
        .addRecord(record)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.stateManager.setPsychologistState(this.psychologistState, false);
    this.processFormData(this.form.value).then((res) => {
      this.setPsychologist(res);
    });
  }
}
